import {
  type FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';

import { useOpenCardsCellMutation } from '@/api/queries/game';
import { type MiniGameType } from '@/common/types/dice-game';
import { type GoodsType } from '@/common/types/goods';
import { type PlantTypeText } from '@/common/types/plant';
import { type ResourceTypeText } from '@/common/types/resource';
import { HeaderAnimationContext } from '@/context/HeaderAnimationContext';
import {
  reduceMiniGameCardOption,
  setActiveBoard,
} from '@/redux/reducers/diceGameReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { Card } from '../Cards/Card/Card';
import { CardsOptions } from '../CardsOptions/CardsOptions';
import { DiceGameContext } from '../context';
import { FlyingImg } from '../DiceAnimation/FlyingDice';
import { DiceGameModalsContext } from '../DiceGameModals/context';
import { GameItemsRewards } from '../GameItemsRewards/GameItemsRewards';
import { parseCellContent, type ParseCellContentReturnType } from '../lib';
import { RewardAnimation } from '../Reward/RewardAnimation';

import css from './styles.module.scss';

const CardsBoard: FC = () => {
  const [delayedRenerCash, setDelayedRenerCash] = useState(0);
  const [collectedCash, setCollectedCash] = useState(false);

  const { cashBalanceRef, setCashBalanceScale } = useContext(
    HeaderAnimationContext,
  );
  const cashFlyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { isFlyingCardsReward, setIsFlyingCardsReward, cardsRewardFromRef } =
    useContext(DiceGameContext);
  const { openBombEvent, openGameOverEvent } = useContext(
    DiceGameModalsContext,
  );
  const [openCell] = useOpenCardsCellMutation({
    fixedCacheKey: 'cards-mini-game-open-cell',
  });
  const isBombEvent = useAppSelector(
    (state) => state.diceGame.miniGame?.extraData?.bomb,
  );
  const isStopEvent = useAppSelector(
    (state) => state.diceGame.miniGame?.extraData?.stop,
  );
  const cardsList = useAppSelector((state) => state.diceGame.miniGame?.board);
  const rewardCollected = useAppSelector(
    (state) => state.diceGame.miniGame?.rewardCollected,
  );
  const kind = useAppSelector((state) => state.diceGame.miniGame?.type) as Omit<
    MiniGameType,
    'tgem' | 'str'
  >;
  const [isCardsEnabledToOpen, setIsCardsEnabledToOpen] = useState(true);
  const changeEnabledToOpenStatus = useCallback((isEnabledToOpen: boolean) => {
    setIsCardsEnabledToOpen(isEnabledToOpen);
  }, []);
  const dispatch = useAppDispatch();
  const getCardReward = useCallback(
    async ({
      cellId,
    }: {
      cellId: string;
    }): Promise<{
      card: ParseCellContentReturnType;
      withBonus?: boolean;
      isCaboom?: boolean;
      isStop?: boolean;
    }> => {
      const { cellContent } = await openCell({ cellId }).unwrap();
      const card = parseCellContent(cellContent);
      dispatch(reduceMiniGameCardOption(card.kind));

      return {
        card,
        withBonus: card.kind === 'doublePrize',
        isCaboom: card.kind === 'bomb',
        isStop: card.kind === 'stop',
      };
    },
    [openCell],
  );

  useEffect(
    () => (): void => {
      if (cashFlyTimeoutRef.current) clearTimeout(cashFlyTimeoutRef.current);
    },
    [],
  );

  useEffect(() => {
    if (isBombEvent) {
      openBombEvent();
    }
  }, [isBombEvent]);

  useEffect(() => {
    if (isStopEvent) {
      openGameOverEvent();
    }
  }, [isStopEvent]);

  useEffect(() => {
    if (isFlyingCardsReward === 'cash') {
      return setDelayedRenerCash(0);
    }

    setDelayedRenerCash(rewardCollected?.cash || 0);
  }, [rewardCollected?.cash, isFlyingCardsReward]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0 } }}
      exit={{ opacity: 0, transition: { duration: 0, delay: 1 } }}
      className="w-[50%] flex flex-col items-center"
    >
      {kind === 'cash' ? (
        <RewardAnimation
          kind="cash"
          value={delayedRenerCash}
          className="mt-[min(3.45svh,_2rem)] w-[9.0625rem] justify-start"
          duration={isFlyingCardsReward ? 2.5 : 1.1}
          delay={isFlyingCardsReward ? 0 : 700}
          hidden={collectedCash}
          scale={isFlyingCardsReward ? 1.4 : 1}
        />
      ) : (
        <GameItemsRewards
          list={Object.entries(rewardCollected || {}).map(([item, value]) => ({
            itemName: item as ResourceTypeText | PlantTypeText | GoodsType,
            value,
          }))}
        />
      )}
      <div className={css.cardList}>
        {cardsList?.map((cardData, index) => (
          <Card
            id={String(index)}
            getCardReward={getCardReward}
            key={`card-${index}`}
            openCardData={
              cardData !== '*' && typeof cardData !== 'number'
                ? parseCellContent(cardData)
                : undefined
            }
            changeEnabledToOpenStatus={changeEnabledToOpenStatus}
            kind={kind}
            disabled={!isCardsEnabledToOpen || cardData !== '*'}
          />
        ))}
      </div>
      <CardsOptions className="mb-[min(3.45svh,_2rem)]" />
      {/* <div className="w-[21.75rem] mb-12">
        <MainButton
          className={cn(!isClaimRewardActive && '!backdrop-blur-0')}
          inactive={!isClaimRewardActive}
          isLoading={isLoading}
          disabled={isLoading || !isClaimRewardActive}
          onClick={collectRewards}
          text={t('Claim')}
        />
      </div> */}
      {isFlyingCardsReward === 'cash' &&
        !collectedCash &&
        createPortal(
          <>
            {Array.from({ length: 8 }).map((_, index) => (
              <FlyingImg
                duration={0.5}
                key={index + 'cash'}
                isFlying={!!isFlyingCardsReward}
                fromRef={cardsRewardFromRef}
                toRef={cashBalanceRef}
                delay={index * 0.2}
                animationType="linear"
                completeCallback={() => {
                  if (index === 7) {
                    setCollectedCash(true);
                    cashFlyTimeoutRef.current = setTimeout(() => {
                      setIsFlyingCardsReward(null);
                      setCashBalanceScale(false);
                      dispatch(setActiveBoard('dice'));
                    }, 300);
                  }
                }}
                startScale={1.4}
                type={'cash'}
                scale={1.6}
              />
            ))}
          </>,
          document.getElementById('root')!,
        )}
    </motion.div>
  );
};

export { CardsBoard };
